/**
 * Video Call Controls
 * Control bar for active calls. Adapts to voice-only mode and exposes
 * Mute, Hold (with hold-music), Camera toggle, End, Add (conference),
 * Transfer (warm/cold), and Minimize.
 */
import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import {
  Mic, MicOff, Video, VideoOff, PhoneOff,
  PauseCircle, PlayCircle, UserPlus, ArrowRightLeft, X, ArrowLeft,
  Volume2, VolumeX
} from 'lucide-react';
import { useVideoCall, CallStatus } from '../../context/VideoCallContext';
import { useAuth } from '../../context/AuthContext';

const VideoCallControls = ({ className = '' }) => {
  const { api, user } = useAuth();
  const {
    callState,
    mediaState,
    toggleMic,
    toggleCamera,
    toggleHold,
    transferCall,
    completeWarmTransfer,
    cancelWarmTransfer,
    addParticipantToCall,
    endCall,
    cancelCall,
    formatDuration
  } = useVideoCall();

  const { status, duration, isCaller, callMode, isOnHold, _pendingWarmTransfer, _isInConference, participants } = callState;
  const { isMicOn, isCameraOn } = mediaState;

  // iter145 — Hold is disabled on any INTERNAL call once a 3rd party
  // joins. Hold music + the "music plays for both" bug only make sense
  // in a strict 2-party context; once the call becomes a conference
  // (Add Participant → Merge, or Warm Transfer in flight), holding one
  // leg silently kills audio to the rest of the room. Product
  // requirement: simply hide the Hold control whenever there are 3+
  // participants or we're actively staging a 3-way call.
  const isMultiParty = (participants?.length || 0) >= 3 || !!_isInConference || !!_pendingWarmTransfer;
  const showHoldButton = !isMultiParty;

  // iter145 — Speaker toggle (voice-only calls on mobile). Internal
  // voice calls go through plain WebRTC; we redirect the remote audio
  // element via setSinkId('default') for speakerphone bias on Android,
  // and toast on iOS (which routes all WebRTC audio through the
  // earpiece by design). Voice-call gating prevents the toggle from
  // appearing on video calls where the screen + Bluetooth context
  // already implies loud-speaker.
  const isVoiceMode = callMode === 'voice';
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  const toggleSpeaker = async () => {
    const next = !isSpeakerOn;
    setIsSpeakerOn(next);
    try {
      // The remote-stream audio element is the <audio> playing the
      // remote peer's track. VideoCallContext attaches it as
      // <audio data-testid="remote-audio">.
      const remoteAudio = document.querySelector('[data-testid="remote-audio"], video[data-testid="remote-video"]');
      if (!remoteAudio || typeof remoteAudio.setSinkId !== 'function') {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints||0) > 1);
        if (isIOS) {
          // Best we can do — surface this so users know to use the iOS-level toggle
          // eslint-disable-next-line no-console
          console.info('[speaker] iOS detected; use iOS Control Center / call drawer to switch output.');
        }
        return;
      }
      await remoteAudio.setSinkId(next ? 'default' : '');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[speaker] setSinkId failed:', e?.message || e);
      setIsSpeakerOn(!next);
    }
  };

  const [pickerMode, setPickerMode] = useState(null); // 'add' | 'warm' | 'cold' | null
  const [pickerOpen, setPickerOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');

  // Pre-fetch the user list on mount so the picker pops open instantly. We
  // refresh it in the background (silent) every time the picker is opened so
  // newly invited teammates show up — but the existing list remains visible
  // and clickable while the refresh is in flight.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/users/all');
        if (!cancelled) setUsers((res.data || []).filter((u) => u.id !== user?.id));
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [api, user?.id]);

  const handleEndClick = () => {
    if (status === CallStatus.CALLING && isCaller) {
      cancelCall();
    } else {
      endCall();
    }
  };

  const openPicker = (mode) => {
    setPickerMode(mode);
    setPickerOpen(true);
    // Background-refresh — does NOT block render, doesn't clear existing list.
    (async () => {
      try {
        const res = await api.get('/users/all');
        setUsers((res.data || []).filter((u) => u.id !== user?.id));
      } catch (e) { /* ignore */ }
    })();
  };

  const handlePick = async (target) => {
    setPickerOpen(false);
    if (pickerMode === 'add') {
      await addParticipantToCall({ targetUserId: target.id });
    } else if (pickerMode === 'cold' || pickerMode === 'warm') {
      await transferCall({ targetUserId: target.id, mode: pickerMode });
    }
    setPickerMode(null);
    setSearch('');
  };

  const isVoiceCall = callMode === 'voice';
  const filtered = (users || []).filter((u) =>
    !search.trim() ||
    `${u.first_name || ''} ${u.last_name || ''} ${u.email || ''}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      {/* Pending warm-transfer / add-participant affordance */}
      {_pendingWarmTransfer && (
        <div className="flex items-center justify-center gap-2 mb-2 p-3 bg-amber-500/95 backdrop-blur-sm rounded-2xl text-black text-xs font-medium" data-testid="warm-transfer-bar">
          <span>
            {_pendingWarmTransfer.uiMode === 'add'
              ? 'Connected to teammate — click "Merge" to bring them onto the line.'
              : 'Briefing — when ready, hand them off:'}
          </span>
          <Button size="sm" className="h-7 px-2" onClick={completeWarmTransfer} data-testid="complete-warm-transfer">
            {_pendingWarmTransfer.uiMode === 'add' ? 'Merge' : 'Complete'}
          </Button>
          <Button size="sm" variant="outline" className="h-7 px-2" onClick={cancelWarmTransfer} data-testid="cancel-warm-transfer">
            Cancel
          </Button>
        </div>
      )}
      <div className={`flex items-center justify-center gap-3 p-4 bg-black/80 backdrop-blur-sm rounded-2xl ${className}`} data-testid="call-controls">
        {/* Duration */}
        {status === CallStatus.CONNECTED && (
          <div className="text-white text-sm font-mono mr-2" data-testid="call-duration">
            {formatDuration(duration)}
          </div>
        )}

        {/* Mute */}
        <Button
          size="icon"
          variant={isMicOn ? 'secondary' : 'destructive'}
          className="h-12 w-12 rounded-full"
          onClick={toggleMic}
          data-testid="toggle-mic-btn"
          title={isMicOn ? 'Mute' : 'Unmute'}
        >
          {isMicOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
        </Button>

        {/* Hold — hidden during multi-party calls (iter145). */}
        {showHoldButton && (
        <Button
          size="icon"
          variant={isOnHold ? 'destructive' : 'secondary'}
          className="h-12 w-12 rounded-full"
          onClick={toggleHold}
          data-testid="toggle-hold-btn"
          title={isOnHold ? 'Resume call' : 'Hold (plays hold music)'}
        >
          {isOnHold ? <PlayCircle className="w-6 h-6" /> : <PauseCircle className="w-6 h-6" />}
        </Button>
        )}

        {/* Speaker toggle — voice-only mode, mobile-first (iter145). */}
        {isVoiceMode && (
          <Button
            size="icon"
            variant={isSpeakerOn ? 'default' : 'secondary'}
            className="h-12 w-12 rounded-full md:hidden"
            onClick={toggleSpeaker}
            data-testid="toggle-speaker-btn"
            title={isSpeakerOn ? 'Speakerphone on (tap for earpiece)' : 'Earpiece (tap for speakerphone)'}
          >
            {isSpeakerOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </Button>
        )}

        {/* Camera (also in voice mode — pressing it turns camera ON, escalating the call) */}
        <Button
          size="icon"
          variant={isCameraOn ? 'secondary' : 'destructive'}
          className="h-12 w-12 rounded-full"
          onClick={toggleCamera}
          data-testid="toggle-camera-btn"
          title={isCameraOn ? 'Turn camera off' : (isVoiceCall ? 'Turn camera on' : 'Turn camera on')}
        >
          {isCameraOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
        </Button>

        {/* Add to call (conference) */}
        <Button
          size="icon"
          variant="secondary"
          className="h-12 w-12 rounded-full"
          onClick={() => openPicker('add')}
          data-testid="add-participant-btn"
          title="Add a participant"
        >
          <UserPlus className="w-5 h-5" />
        </Button>

        {/* Transfer */}
        <Button
          size="icon"
          variant="secondary"
          className="h-12 w-12 rounded-full"
          onClick={() => openPicker('cold')}
          data-testid="transfer-btn"
          title="Transfer call"
        >
          <ArrowRightLeft className="w-5 h-5" />
        </Button>

        {/* End */}
        <Button
          size="icon"
          variant="destructive"
          className="h-14 w-14 rounded-full"
          onClick={handleEndClick}
          data-testid="end-call-btn"
        >
          <PhoneOff className="w-6 h-6" />
        </Button>
      </div>

      {/* Inline picker overlay — same z-stack as call container */}
      {pickerOpen && (
        <div
          className="fixed inset-0 z-[10000] bg-black/80 flex items-end"
          data-testid="call-picker"
          onClick={() => { setPickerOpen(false); setPickerMode(null); }}
        >
          <div
            className="w-full bg-white dark:bg-[#1a1a1a] rounded-t-3xl p-4 max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-3">
              <Button size="icon" variant="ghost" onClick={() => { setPickerOpen(false); setPickerMode(null); }} data-testid="picker-close">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h3 className="font-semibold text-base">
                {pickerMode === 'add' ? 'Add to call' : 'Transfer call'}
              </h3>
              <button className="ml-auto" onClick={() => { setPickerOpen(false); setPickerMode(null); }}>
                <X className="w-5 h-5" />
              </button>
            </div>
            {pickerMode !== 'add' && (
              <div className="flex gap-2 mb-3">
                <Button
                  size="sm"
                  variant={pickerMode === 'cold' ? 'default' : 'outline'}
                  onClick={() => setPickerMode('cold')}
                  data-testid="transfer-cold-btn"
                >Cold Transfer</Button>
                <Button
                  size="sm"
                  variant={pickerMode === 'warm' ? 'default' : 'outline'}
                  onClick={() => setPickerMode('warm')}
                  data-testid="transfer-warm-btn"
                >Warm Transfer</Button>
              </div>
            )}
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search teammates"
              className="h-9 w-full rounded-lg border bg-background px-2 text-sm mb-2"
              style={{ fontSize: '16px' }}
              data-testid="picker-search"
            />
            <div className="flex-1 overflow-y-auto space-y-1">
              {filtered.map((u) => (
                <button
                  key={u.id}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted text-sm"
                  onClick={() => handlePick(u)}
                  data-testid={`picker-user-${u.id}`}
                >
                  <span className="font-medium">{u.first_name} {u.last_name}</span>
                  <span className="ml-2 text-xs text-muted-foreground">{u.role}</span>
                </button>
              ))}
              {filtered.length === 0 && <p className="text-center text-xs text-muted-foreground py-4">No matches.</p>}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VideoCallControls;
